import React, { createContext, useContext, useState } from 'react';
import { SharedUIContextType, Theme, ThemeMode, StatusType, StatusIndicatorWrapperProps } from '../types.js';
import { StatusIndicator } from '../components/StatusIndicator.js';

const defaultTheme: Theme = {
  mode: 'dark',
  colors: {
    primary: '#6366f1',    // Indigo
    secondary: '#8b5cf6',  // Purple
    accent: '#10b981',     // Emerald
    background: '#0f172a', // Dark blue
    text: '#f3f4f6',      // Gray-100
    border: '#374151'      // Gray-700
  }
};

const SharedUIContext = createContext<SharedUIContextType | undefined>(undefined);

export const useSharedUI = () => {
  const context = useContext(SharedUIContext);
  if (context === undefined) {
    throw new Error('useSharedUI must be used within a SharedUIProvider');
  }
  return context;
};

export const SharedUIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [theme] = useState<Theme>(defaultTheme);
  const [tooltipContent, setTooltipContent] = useState('');
  const [themeMode, setThemeMode] = useState<ThemeMode>('dark');
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  const showTooltip = (content: string) => {
    setTooltipContent(content);
  };

  const hideTooltip = () => {
    setTooltipContent('');
  };

  const statusToType = (status: string): StatusType => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'active';
      case 'inactive':
        return 'inactive';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'resolved':
        return 'resolved';
      case 'investigating':
        return 'investigating';
      case 'running':
        return 'running';
      default:
        return 'inactive';
    }
  };

  const value: SharedUIContextType = {
    themeMode,
    setThemeMode,
    isSidebarOpen,
    setSidebarOpen,
    theme,
    StatusIndicator: ({ status, size = 'md', animate = false, ...props }: StatusIndicatorWrapperProps) => (
      <StatusIndicator type="system" status={statusToType(status)} size={size} animate={animate} {...props} />
    ),
    isModalOpen,
    setIsModalOpen,
    showTooltip,
    hideTooltip
  };

  return (
    <SharedUIContext.Provider value={value}>
      {children}
      {tooltipContent && (
        <div className="fixed z-[9999] px-2 py-1 text-sm text-white bg-gray-800 rounded shadow-lg">
          {tooltipContent}
        </div>
      )}
    </SharedUIContext.Provider>
  );
};
